/**
 * @boli/core - Stream Service
 *
 * Platform-agnostic streaming service for Boli Live.
 * Handles live video streaming for auctions using Amazon IVS.
 * For MVP: Uses test stream URLs; production will use real IVS channels.
 */

import type {
  Stream,
  StreamStatus,
  StreamInfo,
  CreateStreamResponse,
  StreamErrorCode,
  STREAM_CONFIG,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IStreamService {
  /**
   * Create a new stream for an auction
   * @param auctionId - The auction to create a stream for
   */
  createStream(auctionId: string): Promise<CreateStreamResponse>;

  /**
   * Get stream info for an auction
   * @param auctionId - The auction ID
   */
  getStreamInfo(auctionId: string): Promise<StreamInfo | null>;

  /**
   * Start streaming (for seller)
   * @param auctionId - The auction ID
   */
  startStream(auctionId: string): Promise<{ success: boolean; error?: string }>;

  /**
   * Stop streaming (for seller)
   * @param auctionId - The auction ID
   */
  stopStream(auctionId: string): Promise<{ success: boolean; error?: string }>;

  /**
   * Report viewer joined (for analytics)
   * @param auctionId - The auction ID
   */
  reportViewerJoined(auctionId: string): Promise<void>;

  /**
   * Report viewer left (for analytics)
   * @param auctionId - The auction ID
   */
  reportViewerLeft(auctionId: string): Promise<void>;
}

export interface StreamServiceConfig {
  apiBaseUrl: string;
  enableLiveVideo?: boolean; // Feature flag for MVP
  testStreamUrl?: string; // Fallback test stream
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_STREAM_CONFIG: StreamServiceConfig = {
  apiBaseUrl: '',
  enableLiveVideo: false, // Off by default for MVP
  testStreamUrl: 'https://d2zihajmogu5jn.cloudfront.net/bipbop-advanced/bipbop_16x9_variant.m3u8',
};

// ============================================================================
// STREAM SERVICE IMPLEMENTATION
// ============================================================================

export class StreamService implements IStreamService {
  private config: StreamServiceConfig;
  private fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  private getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<StreamServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_STREAM_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Create a new stream for an auction
   */
  async createStream(auctionId: string): Promise<CreateStreamResponse> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return {
          success: false,
          error: {
            code: 'UNAUTHORIZED',
            message: 'Authentication required',
          },
        };
      }

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/streams/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ auctionId }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: (data.code as StreamErrorCode) || 'STREAM_CREATION_FAILED',
            message: data.message || 'Failed to create stream',
          },
        };
      }

      return {
        success: true,
        stream: data.stream,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'STREAM_CREATION_FAILED',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get stream info for an auction
   */
  async getStreamInfo(auctionId: string): Promise<StreamInfo | null> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/streams/${auctionId}`
      );

      if (!response.ok) {
        // No stream or error - return fallback for MVP
        if (!this.config.enableLiveVideo) {
          return {
            playbackUrl: this.config.testStreamUrl,
            isLive: false,
            viewerCount: 0,
          };
        }
        return null;
      }

      const data = await response.json();

      return {
        playbackUrl: data.playbackUrl || this.config.testStreamUrl,
        isLive: data.status === 'live',
        viewerCount: data.viewerCount || 0,
        quality: data.quality,
        latency: data.latency,
      };
    } catch (error) {
      console.error('Failed to get stream info:', error);
      // Return fallback for MVP
      return {
        playbackUrl: this.config.testStreamUrl,
        isLive: false,
        viewerCount: 0,
      };
    }
  }

  /**
   * Start streaming (for seller)
   */
  async startStream(auctionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, error: 'Authentication required' };
      }

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/streams/${auctionId}/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        return { success: false, error: data.message || 'Failed to start stream' };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Stop streaming (for seller)
   */
  async stopStream(auctionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, error: 'Authentication required' };
      }

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/streams/${auctionId}/stop`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        return { success: false, error: data.message || 'Failed to stop stream' };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Report viewer joined (for analytics)
   */
  async reportViewerJoined(auctionId: string): Promise<void> {
    try {
      await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/streams/${auctionId}/viewer-joined`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
    } catch {
      // Silent fail for analytics
    }
  }

  /**
   * Report viewer left (for analytics)
   */
  async reportViewerLeft(auctionId: string): Promise<void> {
    try {
      await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/streams/${auctionId}/viewer-left`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
    } catch {
      // Silent fail for analytics
    }
  }

  /**
   * Check if live video is enabled
   */
  isLiveVideoEnabled(): boolean {
    return !!this.config.enableLiveVideo;
  }

  /**
   * Get test stream URL for MVP fallback
   */
  getTestStreamUrl(): string {
    return this.config.testStreamUrl || DEFAULT_STREAM_CONFIG.testStreamUrl!;
  }
}

// ============================================================================
// STREAM SERVICE FACTORY
// ============================================================================

let streamServiceInstance: StreamService | null = null;

export function createStreamService(
  config: Partial<StreamServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): StreamService {
  streamServiceInstance = new StreamService(config, fetchAdapter, getAuthToken);
  return streamServiceInstance;
}

export function getStreamService(): StreamService {
  if (!streamServiceInstance) {
    throw new Error('StreamService not initialized. Call createStreamService first.');
  }
  return streamServiceInstance;
}
